import React from 'react'
import { AxiosError, isAxiosError } from 'axios'
import * as Sentry from '@sentry/react'

import Button from '@components/Button'
import Text from '@components/Text'

import i18n from '@src/i18n'
import { toast } from '@helpers/toaster'
import { ERROR_CODE_I18N_MAP } from '@helpers/ErrorHandler/errorCodeMap'
import { resolveIntegrationErrorMessage } from '@helpers/ErrorHandler/integrationError'
import { extractErrorMessages } from '@helpers/ErrorHandler/utils'
import { isIntercomAvailable, safeIntercom } from '@helpers/Intercom/safe'

/**
 * Class for handling API request errors with proper formatting and reporting
 */

export type ErrorResponse = {
  details: { exception: string | Record<string, unknown>; [key: string]: unknown } | Record<string, unknown>
  error_code: string
  message: string | Record<string, unknown>
  sentry_id?: string
  status_code: 500
}

export class RequestErrorHandler {
  private error: unknown
  private errorText?: string
  private errorCode?: string
  private errorMessage: string | null = ''
  private reportButton: React.ReactNode = null
  private sentryCaptureId: string | null = null

  /**
   * Create a new error handler instance
   * @param error - The error object to handle
   * @param errorText - Optional custom error text
   */
  constructor(error: unknown, errorText?: string) {
    this.error = error
    this.errorText = errorText
  }

  /**
   * Process the error and show toast notification
   * @returns Formatted error message
   */
  public handle(): string | null {
    if (!this.error) {
      return null
    }

    this.logErrorIfEnabled()
    this.extractErrorMessage()
    this.captureInSentry()
    this.createReportButton()
    this.showErrorToast()

    return this.getFormattedErrorMessage()
  }

  /**
   * Log error to console if logging is enabled
   */
  private logErrorIfEnabled(): void {
    if (import.meta.env.VITE_LOGGER === '1') {
      console.error(this.error)
    }
  }

  /**
   * Capture exception in Sentry
   */
  private async captureInSentry(): Promise<void> {
    if (this.errorCode) {
      this.sentryCaptureId = Sentry.captureException(this.error, {
        tags: { errorCode: this.errorCode },
      })
    } else {
      this.sentryCaptureId = Sentry.captureException(this.error)
    }
  }

  /**
   * Extract error message from Axios error response
   */
  private extractErrorMessageFromAxiosError(error: AxiosError<ErrorResponse>): void {
    const res = error.response?.data

    if (res?.error_code) {
      this.errorCode = res.error_code
      const i18nKey = ERROR_CODE_I18N_MAP[res.error_code]
      if (i18nKey) {
        this.errorMessage = i18n.t(i18nKey) as string
        return
      }
    }

    // INTG.BL.024 (IntegrationSyncBlockedApproval) follows the
    // integration-error pattern: the backend signals integration + reason_code
    // in details, and we render a localized string. See
    // approveit-backend/integration/softledger/error_mapping.py for the
    // backend-side contract.
    if (res?.error_code === 'INTG.BL.024' && res?.details) {
      this.errorMessage = resolveIntegrationErrorMessage(
        res.details as Record<string, unknown>,
      )
      return
    }

    if (res?.details || res?.message) {
      if (typeof res?.message === 'object') {
        this.errorMessage = extractErrorMessages(res?.message).join('\n')
      } else {
        this.errorMessage = res?.message
      }

      if (this.errorCode) {
        this.errorMessage = `${this.errorCode}: ${this.errorMessage}`
      }

      if (res.details?.exception) {
        this.errorMessage = `${this.errorMessage}\n${res.details.exception}`
      } else if (res.details && !res.details?.exception) {
        this.errorMessage = `${this.errorMessage}\n${extractErrorMessages(res.details).join('\n')}`
      }
    } else if (typeof res === 'object') {
      this.errorMessage = extractErrorMessages(res).join('\n')
    }

    if (!this.errorMessage) {
      this.errorMessage = error.message
    }
  }

  /**
   * Extract error message from various error types
   */
  private extractErrorMessage(): void {
    const isErrorFromAxios = isAxiosError(this.error)

    if (isErrorFromAxios) {
      this.extractErrorMessageFromAxiosError(this.error as AxiosError<ErrorResponse>)
    } else if (this.error instanceof Error) {
      this.errorMessage = this.error.message
    }
  }

  /**
   * Create report error button for Intercom
   */
  private createReportButton(): void {
    if (!isIntercomAvailable()) {
      return
    }

    let sentryMessage = ''

    if (this.error instanceof AxiosError && this.error.response?.data?.sentry_id) {
      sentryMessage += `\n\nBack-end error ID: ${this.error.response?.data?.sentry_id}`
    }
    if (this.sentryCaptureId) {
      sentryMessage += `\n\nFront-end error ID: ${this.sentryCaptureId}`
    }

    if (sentryMessage) {
      const handleReportClick = () => {
        const message = `Hi Approveit Team, I've encountered an error in the app. Below are the details of the issue.\n${this.errorMessage}${sentryMessage}`
        safeIntercom('showNewMessage', message)
      }

      this.reportButton = (
        <Button onClick={handleReportClick} type="outlined" className="w-full">
          Report error
        </Button>
      )
    }
  }

  /**
   * Show error toast notification
   */
  private showErrorToast(): void {
    toast.error(
      <div className="flex flex-col gap-4">
        <Text text={this.getFormattedErrorMessage()} />
        {this.reportButton}
      </div>,
    )
  }

  /**
   * Get formatted error message
   * @returns Formatted error message string
   */
  private getFormattedErrorMessage(): string {
    return (
      this.errorMessage?.replace(/\n\n/g, '\n') ||
      this.errorText ||
      (i18n.t('common:toast.somethingWentWrong') as string)
    )
  }
}
